import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '@material-ui/core';
import {
  CalendarDayRoleGroupData,
  CalendarViewData,
  DotKind,
  getCalendarDayData,
  getFrequencyDotTooltip,
  getFullCalendarDateKey,
  getFullCalendarDayNumber,
} from '../data';
import { EventModalData } from './EventModal';

const DayCellContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

/**
 * Fixed pixel sizes: FullCalendar's cell font is ~10px inside the widget, so
 * em-based sizing rendered dots too small to read.
 */
const DOT_DIAMETER_PX = 30;
const DOT_FONT_PX = 13;

/**
 * Dot backgrounds are fixed colors in every theme, so the label is always
 * black for contrast (white on green/yellow/gray fails readability).
 */
export const EventDot = styled.div<{ $statusColor: string }>`
  background-color: ${(props) => props.$statusColor};
  color: #000;
  font-weight: bold;
  flex: 0 0 auto;
  height: ${DOT_DIAMETER_PX}px;
  width: ${DOT_DIAMETER_PX}px;
  border-radius: 50%;
  font-size: ${DOT_FONT_PX}px;
  text-align: center;
  line-height: ${DOT_DIAMETER_PX - 2}px;
  border: solid 1px ${(props) => props.theme.palette.text.primary};
  box-sizing: border-box;
  cursor: pointer;
`;

const DateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

const DAY_NUMBER_FONT_PX = 16;

/** Days from the previous/next month render like current-month days (no dimming). */
const DayNumber = styled.span`
  display: block;
  font-size: ${DAY_NUMBER_FONT_PX}px;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  line-height: 1.3;
  margin-bottom: 2px;
  color: ${(props) => props.theme.palette.text.primary};
`;

type DayCellRendererProps = {
  calendarViewData: CalendarViewData | null;
  date: Date;
  setModalOpen: (data: EventModalData) => void;
  filteringRoleGroups: number[];
  onDotHighlight: (
    cpfId: number,
    periodStart: string,
    periodEnd: string,
    planWeekKey: string,
  ) => void;
};

const shouldShowRoleGroup = (
  roleGroupId: string,
  filteringRoleGroups: number[],
): boolean => {
  if (!filteringRoleGroups.length || filteringRoleGroups[0] === 0) {
    return true;
  }

  return filteringRoleGroups.some(
    (roleGroupFilterId) => roleGroupId === roleGroupFilterId?.toString(),
  );
};

/**
 * green = completed visit, yellow = in-progress visit, gray = planned (not yet
 * completed). All are solid so they read the same on light and dark themes.
 */
export const DOT_COLORS: Record<DotKind, string> = {
  green: '#4caf50',
  yellow: '#ffeb3b',
  gray: '#c8c8c8',
};

const renderDot = (
  roleGroupData: CalendarDayRoleGroupData,
  date: Date,
  setModalOpen: (data: EventModalData) => void,
  onDotHighlight: (
    cpfId: number,
    periodStart: string,
    periodEnd: string,
    planWeekKey: string,
  ) => void,
) => {
  const { dotKind } = roleGroupData;
  const visit = roleGroupData.visits[0];
  const isVisitDot = dotKind !== 'gray';

  if (isVisitDot && !visit) {
    return null;
  }

  const tooltipTitle = getFrequencyDotTooltip(
    roleGroupData.roleGroupName,
    roleGroupData.frequencyLabel ?? '',
  );

  const color = DOT_COLORS[dotKind] ?? DOT_COLORS.gray;

  const handleClick = () => {
    if (isVisitDot && visit) {
      setModalOpen({
        visit,
        roleGroupName: roleGroupData.roleGroupName,
        frequencyLabel: roleGroupData.frequencyLabel ?? '',
        dateKey: getFullCalendarDateKey(date),
      });
    }
    if (roleGroupData.cpfId) {
      onDotHighlight(
        roleGroupData.cpfId,
        roleGroupData.periodStart,
        roleGroupData.periodEnd,
        roleGroupData.planWeekKey,
      );
    }
  };

  return (
    <Tooltip
      key={roleGroupData.dotKey}
      title={tooltipTitle || roleGroupData.roleGroupName}
    >
      <EventDot
        $statusColor={color}
        onClick={handleClick}
        data-sv-dot={dotKind}
        data-sv-role={roleGroupData.roleGroupId}
      >
        {roleGroupData.roleAbbrev}
      </EventDot>
    </Tooltip>
  );
};

export const DayCellRenderer = ({
  calendarViewData,
  date,
  setModalOpen,
  filteringRoleGroups,
  onDotHighlight,
}: DayCellRendererProps) => {
  const dayNum = getFullCalendarDayNumber(date);
  const dayData = getCalendarDayData(calendarViewData, date);

  return (
    <DayCellContent>
      <DayNumber data-sv-day-number={dayNum}>{dayNum}</DayNumber>
      <DateRow>
        {dayData?.roleGroups
          .filter((roleGroupData) =>
            shouldShowRoleGroup(roleGroupData.roleGroupId, filteringRoleGroups),
          )
          .map((roleGroupData) =>
            renderDot(roleGroupData, date, setModalOpen, onDotHighlight),
          )}
      </DateRow>
    </DayCellContent>
  );
};
